import React, { useEffect, useState } from "react";
import { Timer, Sparkles, X, CalendarClock } from "lucide-react";

function useCountdown(endsAt) {
  const [ms, setMs] = useState(() => new Date(endsAt) - new Date());
  useEffect(() => {
    const t = setInterval(() => setMs(new Date(endsAt) - new Date()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return ms;
}

function PromoCard({ promo, onExpire, onDismiss, reservationsEnabled, onReserve }) {
  const ms = useCountdown(promo.ends_at);
  useEffect(() => { if (ms <= 0) onExpire(promo.id); }, [ms, promo.id, onExpire]);
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60); const s = total % 60;
  const urgent = total <= 60;
  return (
    <div data-testid={`promo-banner-${promo.id}`} className={`relative overflow-hidden rounded-2xl border-2 ${urgent ? "border-[#E05328]" : "border-[#D99B26]"} bg-gradient-to-r from-[#D99B26] via-[#E05328] to-[#C85A32] text-white p-4 shadow-lg fade-in-up`}>
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "8px 8px" }}/>
      <button data-testid={`promo-dismiss-${promo.id}`} onClick={() => onDismiss(promo.id)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center z-10"><X size={13}/></button>
      <div className="relative flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur grid place-items-center flex-shrink-0">
          <Sparkles size={20} className="animate-pulse"/>
        </div>
        <div className="flex-1 min-w-0 pr-8">
          <div className="text-[10px] uppercase tracking-widest font-black opacity-90">🍴 Promo del locale</div>
          <div className="font-serif-display text-xl font-black leading-tight mt-0.5">{promo.title}</div>
          {promo.description && <div className="text-sm mt-1 opacity-95">{promo.description}</div>}
        </div>
      </div>
      <div className="relative mt-3 flex items-center gap-2 flex-wrap">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono font-black text-sm ${urgent ? "bg-[#231F1D] text-[#FFD84C] animate-pulse" : "bg-[#231F1D]/90 text-[#FFD84C]"}`}>
          <Timer size={13}/> {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </div>
        <div className="text-[11px] opacity-90 font-semibold flex-1 min-w-0">rimasti · affrettati!</div>
        {reservationsEnabled && (
          <button data-testid={`promo-reserve-${promo.id}`} onClick={() => onReserve(promo)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-[#C85A32] text-[11px] font-black hover:bg-[#FFF7E6] shadow">
            <CalendarClock size={12}/> Prenota per approfittarne
          </button>
        )}
      </div>
    </div>
  );
}

export default function PromoBanner({ slug, initial, reservationsEnabled, onReserve }) {
  const [promos, setPromos] = useState(initial || []);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(`vm_promo_dismiss_${slug}`) || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => { setPromos(initial || []); }, [initial]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/menu/${slug}/promotions`);
        const data = await res.json();
        if (alive) setPromos(data.promotions || []);
      } catch {}
    };
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [slug]);

  const dismiss = (id) => {
    setDismissed((prev) => {
      const nxt = new Set(prev); nxt.add(id);
      sessionStorage.setItem(`vm_promo_dismiss_${slug}`, JSON.stringify([...nxt]));
      return nxt;
    });
  };
  const expire = (id) => setPromos((prev) => prev.filter((p) => p.id !== id));

  const visible = promos.filter((p) => !dismissed.has(p.id));
  if (visible.length === 0) return null;

  return (
    <div className="px-5 pt-4 space-y-2">
      {visible.map((p) => <PromoCard key={p.id} promo={p} onExpire={expire} onDismiss={dismiss} reservationsEnabled={reservationsEnabled} onReserve={onReserve}/>)}
    </div>
  );
}
